import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  directoryApi,
  type AppNameValue,
  type CreateTeamRequest,
  type ReplaceUserTeamsRequest,
  type Team,
  type UpdateTeamRequest,
} from '../client';

/** Stable query-key factory for team caches. */
export const teamKeys = {
  all: ['directory', 'teams'] as const,
  byOrgAndApp: (organisationId: string, app: AppNameValue) =>
    ['directory', 'teams', 'org', organisationId, app] as const,
};

/**
 * Fetch teams for the given organisation + app. Drives the Teams tab
 * grid; members are inlined in the response so per-team round-trips
 * aren't needed for badge / count rendering.
 */
export function useTeams(
  organisationId: string | null,
  app: AppNameValue,
  options?: Omit<UseQueryOptions<Team[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Team[], Error>({
    queryKey: organisationId
      ? teamKeys.byOrgAndApp(organisationId, app)
      : teamKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getTeams(organisationId, app);
    },
    enabled: !!organisationId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/** Mutation wrapper around POST `/teams`. Invalidates the org-app list. */
export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateTeamRequest>({
    mutationKey: [...teamKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createTeam(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({
        queryKey: teamKeys.byOrgAndApp(vars.organisationId, vars.app),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/teams`. Caller passes
 * `organisationId` + `app` alongside the body so the per-org-per-app
 * cache key invalidates correctly (the wire body itself only carries
 * `teamId`).
 */
export function useUpdateTeam() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; app: AppNameValue; body: UpdateTeamRequest }
  >({
    mutationKey: [...teamKeys.all, 'update'],
    mutationFn: ({ body }) => directoryApi.updateTeam(body),
    onSuccess: (_id, { organisationId, app }) => {
      qc.invalidateQueries({ queryKey: teamKeys.byOrgAndApp(organisationId, app) });
    },
  });
}

/** Mutation wrapper around DELETE `/teams/{id}`. */
export function useDeleteTeam() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { organisationId: string; app: AppNameValue; id: string }
  >({
    mutationKey: [...teamKeys.all, 'delete'],
    mutationFn: ({ id }) => directoryApi.deleteTeam(id),
    onSuccess: (_res, { organisationId, app }) => {
      qc.invalidateQueries({ queryKey: teamKeys.byOrgAndApp(organisationId, app) });
    },
  });
}

/**
 * Replace a user's team memberships within an (org, app) scope. Used
 * by the Users editor when an admin ticks/unticks teams in the Teams
 * multi-select. Invalidates the org-app teams cache so the Teams tab
 * sees the diff on its next read.
 */
export function useReplaceUserTeams() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { userId: string; body: ReplaceUserTeamsRequest }
  >({
    mutationKey: [...teamKeys.all, 'replace-user-teams'],
    mutationFn: ({ userId, body }) => directoryApi.replaceUserTeams(userId, body),
    onSuccess: (_res, { body }) => {
      qc.invalidateQueries({
        queryKey: teamKeys.byOrgAndApp(body.organisationId, body.app),
      });
    },
  });
}
